import axios from 'axios';
import toast from 'react-hot-toast';
import { REACT_APP_API_BASE_URL, isDevelopment } from '../config/env';
import {
  assertNotRateLimited,
  handleRateLimitResponse,
} from './rateLimitError';

/**
 * API root must include `/api`.
 * Configure per environment in `.env.development` / `.env.production` (see `.env.example`).
 */
const apiBaseURL = REACT_APP_API_BASE_URL;

if (isDevelopment) {
  console.log('DEBUG [apiBaseURL]:', apiBaseURL, {
    envFile: process.env.REACT_APP_API_BASE_URL,
  });
}

const REFRESH_TOKEN_KEY = 'refreshToken';

// Create a base axios instance with common configuration
const api = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const ADMIN_FCM_DEVICE_ID_KEY = 'vedika_admin_fcm_device_id';

const unregisterAdminFcmBeforeClientLogout = async () => {
  const token = localStorage.getItem('token');
  const deviceId = localStorage.getItem(ADMIN_FCM_DEVICE_ID_KEY);
  if (!token || !deviceId) return;
  try {
    await axios.delete(
      `${api.defaults.baseURL}/master-admin/notifications/fcm-token/${encodeURIComponent(deviceId)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch {
    // best-effort cleanup
  }
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('user');
  localStorage.removeItem('userId');
  sessionStorage.removeItem('isLoggedIn');
};

const redirectToLoginIfNeeded = () => {
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

const getTokenExpirySeconds = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

/** Refresh when expired or within 5 minutes of expiry. */
const shouldRefreshToken = (token: string): boolean => {
  const exp = getTokenExpirySeconds(token);
  if (exp == null) return false;
  const now = Date.now() / 1000;
  return exp <= now + 300;
};

const isRefreshAuthFailure = (error: unknown): boolean => {
  const status = (error as any)?.response?.status;
  return status === 401 || status === 403;
};

const isTransientRefreshError = (error: unknown): boolean => {
  if (isRefreshAuthFailure(error)) return false;
  const code = (error as any)?.code;
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true;
  const message = String((error as any)?.message || '').toLowerCase();
  return (
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('failed to fetch')
  );
};

const isTokenExpired = (token: string): boolean => {
  const exp = getTokenExpirySeconds(token);
  if (exp == null) return true;
  return exp < Date.now() / 1000;
};

const isAuthBypassRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/master-admin/login') || url.includes('/master-admin/auth/refresh-token');
};

const persistTokens = (accessToken: string, refreshToken?: string | null) => {
  localStorage.setItem('token', accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

const refreshAccessToken = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }) as Promise<string>;
  }

  isRefreshing = true;

  try {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const storedAccessToken = localStorage.getItem('token');

    if (!storedRefreshToken && !storedAccessToken) {
      throw new Error('No tokens available');
    }

    let lastError: unknown = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const refreshResponse = await axios.post(
          `${api.defaults.baseURL}/master-admin/auth/refresh-token`,
          storedRefreshToken
            ? { refreshToken: storedRefreshToken }
            : { token: storedAccessToken }
        );

        const { token: newToken, refreshToken: newRefreshToken } = refreshResponse.data;
        persistTokens(newToken, newRefreshToken);
        processQueue(null, newToken);
        return newToken;
      } catch (error) {
        lastError = error;
        if (!isTransientRefreshError(error) || attempt === 1) {
          throw error;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 750));
      }
    }

    throw lastError ?? new Error('Token refresh failed');
  } catch (refreshError) {
    processQueue(refreshError, null);

    // Only clear session when refresh token is rejected — keep admin logged in on network blips.
    if (isRefreshAuthFailure(refreshError)) {
      await unregisterAdminFcmBeforeClientLogout();
      clearAuthStorage();
      redirectToLoginIfNeeded();
    }

    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

// Add a request interceptor
api.interceptors.request.use(
  async (config) => {
    const blockedError = assertNotRateLimited();
    if (blockedError) {
      toast.error(blockedError.message, {
        duration: Math.min(blockedError.retryAfterSeconds * 1000, 30000),
      });
      return Promise.reject(blockedError);
    }

    // Let the browser set multipart boundary — default application/json breaks file uploads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers as any;
      if (headers) {
        if (typeof headers.delete === 'function') {
          headers.delete('Content-Type');
        } else {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      }
    }

    if (isAuthBypassRequest(config.url)) {
      return config;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return config;
    }

    if (shouldRefreshToken(token)) {
      try {
        const newToken = await refreshAccessToken();
        config.headers['Authorization'] = `Bearer ${newToken}`;
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    } else {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle common error cases
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 429) {
      const rateLimitError = handleRateLimitResponse(error);
      toast.error(rateLimitError.message, {
        duration: Math.min(rateLimitError.retryAfterSeconds * 1000, 30000),
      });
      return Promise.reject(rateLimitError);
    }

    const code = error.response?.data?.code;
    const hasStoredToken = Boolean(localStorage.getItem('token'));
    const shouldTryRefresh =
      (status === 401 || code === 'TOKEN_EXPIRED') &&
      !originalRequest?._retry &&
      hasStoredToken &&
      !isAuthBypassRequest(originalRequest?.url);

    if (shouldTryRefresh) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (status === 401 && !isAuthBypassRequest(originalRequest?.url)) {
      const hasSession =
        Boolean(localStorage.getItem('token')) ||
        Boolean(localStorage.getItem(REFRESH_TOKEN_KEY));

      // If refresh already ran and failed transiently, keep the session alive.
      if (originalRequest?._retry) {
        if (!hasSession) {
          redirectToLoginIfNeeded();
        }
      } else if (!hasSession) {
        redirectToLoginIfNeeded();
      }
    }

    if (process.env.NODE_ENV === 'development' && error.response) {
      const method = String(originalRequest?.method || 'GET').toUpperCase();
      const url = originalRequest?.url || 'unknown';
      console.error('[API error]', method, url, error.response.status, error.response.data);
    } else if (process.env.NODE_ENV === 'development' && error.request && !error.response) {
      console.error('[API network error]', originalRequest?.url, error.message);
    }

    return Promise.reject(error);
  }
);

export default api;

export const postBlogToFacebook = async (data: {
  title: string;
  message: string;
  link: string;
  imageUrl: string;
  categoryId: string;
}) => {
  return api.post('/blogs/post', data);
};

export const fetchBlogs = async () => {
  try {
    console.log('Fetching all blogs from:', `${api.defaults.baseURL}/blogs/getBlogs`);
    const response = await api.get('/blogs/getBlogs');
    console.log('All blogs response:', response.data);
    return response;
  } catch (error) {
    console.error('Error fetching all blogs:', error);
    throw error;
  }
};

export const fetchBlogsByCategory = async (categoryId: string) => {
  try {
    const id = typeof categoryId === 'string' ? categoryId : String(categoryId ?? '');
    console.log('Fetching blogs for category:', id, 'from:', `${api.defaults.baseURL}/blogs/category?ids=${id}`);
    const response = await api.get('/blogs/category', {
      params: { ids: id },
    });
    console.log('Category blogs response:', response.data);
    return response;
  } catch (error) {
    console.error('Error fetching blogs by category:', error);
    throw error;
  }
};

export const deleteBlogPost = async (postId: string) => {
  return api.delete(`/blogs/post/${postId}`);
};

export const updateBlogPost = async (facebookPostId: string, data: {
  title: string;
  message: string;
  link: string;
  imageUrl: string;
  categoryId: string;
}) => {
  return api.post(`/blogs/post/${facebookPostId}`, data);
};

export const registerEmployee = async (data: FormData) => {
  return api.post('/master-admin/register', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const updateEmployee = async (id: string, data: FormData) => {
  return api.put(`/master-admin/employees/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteEmployeeById = async (id: string) => {
  return api.delete(`/master-admin/employees/${id}`);
};

export const refreshToken = async (): Promise<boolean> => {
  try {
    await refreshAccessToken();
    return true;
  } catch (error) {
    console.error('Manual token refresh failed:', error);
    return false;
  }
};

export { isTokenExpired, shouldRefreshToken };
